import { useState, useRef, lazy, Suspense } from 'react'
import { createOwnedGallery, initializeGallery } from '../lib/api'
import LoadingDots from './LoadingDots'

const LocationPicker = lazy(() => import('./LocationPicker'))

interface GalleryCreateFormProps {
  galleryId?: string
  initialEmail?: string
  onDone: (result: { id: string; shortCode?: string }) => void
  onAbort?: () => void
  onClose?: () => void
}

const STEPS = ['Navn', 'Sted', 'Forsidebilde'] as const
type Step = 0 | 1 | 2

export default function GalleryCreateForm({
  galleryId,
  initialEmail,
  onDone,
  onAbort,
  onClose,
}: GalleryCreateFormProps) {
  const isClaim = !!galleryId
  const needsEmail = isClaim && !initialEmail

  const [step, setStep] = useState<Step>(0)
  const [name, setName] = useState('')
  const [email, setEmail] = useState(initialEmail ?? '')
  const [lat, setLat] = useState<number | null>(null)
  const [lng, setLng] = useState<number | null>(null)
  const [coverFile, setCoverFile] = useState<File | null>(null)
  const [coverPreview, setCoverPreview] = useState<string | null>(null)
  const [converting, setConverting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    if (!file) return
    setConverting(true)
    try {
      const { isHeic, heicTo } = await import('heic-to')
      if (await isHeic(file)) {
        const blob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.82 })
        const name = file.name.replace(/\.[^.]+$/, '.jpg')
        setCoverFile(new File([blob], name, { type: 'image/jpeg' }))
        setCoverPreview(URL.createObjectURL(blob))
      } else {
        setCoverFile(file)
        setCoverPreview(URL.createObjectURL(file))
      }
    } catch {
      setCoverFile(file)
      setCoverPreview(URL.createObjectURL(file))
    } finally {
      setConverting(false)
    }
  }

  const handleSubmit = async () => {
    if (!coverFile || lat == null || lng == null) return
    if (needsEmail && !email.trim()) return

    setSaving(true)
    setError(null)
    try {
      if (isClaim) {
        await initializeGallery(galleryId, email.trim(), name.trim(), lat, lng, coverFile)
        onDone({ id: galleryId })
      } else {
        const result = await createOwnedGallery(name.trim(), lat, lng, coverFile)
        onDone(result)
      }
    } catch (err) {
      setError((err as Error).message)
      setSaving(false)
    }
  }

  const canAdvance: boolean =
    step === 0
      ? name.trim().length > 0 && (!needsEmail || email.trim().length > 0)
      : step === 1
      ? lat != null && lng != null
      : coverFile != null && !converting

  const goNext = () => {
    if (step < 2) setStep((step + 1) as Step)
    else handleSubmit()
  }

  const goBack = () => {
    if (step > 0) setStep((step - 1) as Step)
  }

  return (
    <>
      {/* Progress dots */}
      <div className="relative flex items-center justify-center gap-2 pb-6 flex-shrink-0">
        {step > 0 && (
          <button
            type="button"
            onClick={goBack}
            className="absolute left-0 text-gray-500 hover:text-white transition-colors p-1 rounded"
            aria-label="Tilbake"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
        )}
        {STEPS.map((_, i) => (
          <div
            key={i}
            className={`rounded-full transition-all duration-300 ${
              i === step
                ? 'w-6 h-2 bg-blue-500'
                : i < step
                ? 'w-2 h-2 bg-blue-400'
                : 'w-2 h-2 bg-gray-600'
            }`}
          />
        ))}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="absolute right-0 text-gray-500 hover:text-white transition-colors p-1 rounded"
            aria-label="Lukk"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Step body */}
      <div className="flex flex-col gap-6 px-0 pb-0">
        {error && (
          <div className="rounded-lg bg-red-900 border border-red-700 p-3 text-red-200 text-sm">
            {error}
          </div>
        )}

        {/* Step 0 — Name (and email for anon claim) */}
        {step === 0 && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-gray-400 text-sm font-medium">Navn på galleriet</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && canAdvance) goNext() }}
                placeholder="Appelsinhaugen"
                autoFocus
                className="bg-gray-800 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
              />
            </div>
            {needsEmail && (
              <div className="flex flex-col gap-1">
                <label className="text-gray-400 text-sm font-medium">Din e-postadresse</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && canAdvance) goNext() }}
                  placeholder="navn@eksempel.no"
                  className="bg-gray-800 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                />
                <p className="text-gray-600 text-xs">Galleriet knyttes til denne epostadressen</p>
              </div>
            )}
            {!needsEmail && initialEmail && (
              <p className="text-gray-500 text-sm">
                Galleriet knyttes til <span className="text-gray-300">{initialEmail}</span>
              </p>
            )}
          </div>
        )}

        {/* Step 1 — Location */}
        {step === 1 && (
          <div className="flex flex-col gap-1">
            <label className="text-gray-400 text-sm font-medium">Velg sted på kartet</label>
            <Suspense fallback={<div className="h-64 rounded-lg bg-gray-800 animate-pulse" />}>
              <LocationPicker
                lat={lat}
                lng={lng}
                onChange={(newLat, newLng) => { setLat(newLat); setLng(newLng) }}
              />
            </Suspense>
          </div>
        )}

        {/* Step 2 — Cover image */}
        {step === 2 && (
          <div className="flex flex-col gap-1">
            <label className="text-gray-400 text-sm font-medium">Forsidebilde</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/heic,image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
            {converting ? (
              <div className="aspect-video rounded-lg overflow-hidden relative bg-gray-900">
                <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/5 to-transparent" />
              </div>
            ) : coverPreview ? (
              <div
                className="relative aspect-video rounded-lg overflow-hidden cursor-pointer group"
                onClick={() => fileInputRef.current?.click()}
              >
                <img src={coverPreview} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                  <span className="text-white opacity-0 group-hover:opacity-100 font-medium text-sm transition-opacity">Endre</span>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="aspect-video rounded-lg border-2 border-dashed border-gray-600 hover:border-gray-400 flex flex-col items-center justify-center gap-2 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-gray-500">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 18.75h18M3 9.75A2.25 2.25 0 015.25 7.5h13.5A2.25 2.25 0 0121 9.75v8.25" />
                </svg>
                <span className="text-gray-500 text-sm">Velg forsidebilde</span>
              </button>
            )}
          </div>
        )}

        {/* Next / Create button */}
        <button
          type="button"
          onClick={goNext}
          disabled={!canAdvance || saving || converting}
          className="
            w-full py-3 rounded-xl
            bg-blue-500 hover:bg-blue-600
            disabled:bg-gray-700 disabled:cursor-not-allowed
            text-white font-semibold
            transition-colors duration-200
            flex items-center justify-center gap-2
          "
        >
          {(saving || converting) && <LoadingDots />}
          {step < 2 ? 'Neste' : saving ? 'Lagrer...' : 'Opprett galleri'}
        </button>

        {step === 0 && onAbort && (
          <button
            type="button"
            onClick={onAbort}
            className="text-gray-500 hover:text-gray-300 text-sm text-center transition-colors w-full"
          >
            Avbryt
          </button>
        )}
      </div>
    </>
  )
}
